/**
 * Lightweight one-shot LLM call used by the import-review flow.
 *
 *   verifyProjectWithAI(jsonText, cfg) → { verdict, summary }
 *
 * No tools, no streaming, no chat history — just "tell me if this is safe."
 * Uses the user's own provider + key (anthropic / openai) so nothing
 * touches our servers.
 */

import { VERIFICATION_PROMPT } from '../security/scanner';
import type { AIConfig } from './storage';

export interface VerifyResult {
  verdict: 'safe' | 'unsafe' | 'unknown';
  summary: string;
}

export async function verifyProjectWithAI(
  projectJson: string,
  cfg: AIConfig,
): Promise<VerifyResult> {
  if (!cfg.apiKey) throw new Error('AI 프로바이더 키가 비어있어요. AI 어시스턴트에서 설정해 주세요.');

  // Cap the JSON we ship to keep token cost down. The scanner already saw
  // the full payload; the LLM is a second opinion.
  const trimmed = projectJson.length > 60_000 ? projectJson.slice(0, 60_000) + '\n…(생략)' : projectJson;
  const userMessage = `${VERIFICATION_PROMPT}\n\n--- BEGIN PROJECT JSON ---\n${trimmed}\n--- END PROJECT JSON ---\n\n응답은 아래 형식으로:\nVERDICT: SAFE | UNSAFE | UNKNOWN\nSUMMARY: <한 문단 요약>\nDETAILS: <필요 시 구체적인 위험 항목>`;

  const text = cfg.provider === 'anthropic'
    ? await callAnthropic(cfg.apiKey, userMessage)
    : await callOpenAI(cfg.apiKey, userMessage);

  return parseVerdict(text);
}

async function callAnthropic(apiKey: string, userMessage: string): Promise<string> {
  const r = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-haiku-4-5',
      max_tokens: 1024,
      messages: [{ role: 'user', content: userMessage }],
    }),
  });
  if (!r.ok) throw new Error(`Anthropic ${r.status}: ${await r.text()}`);
  const data = (await r.json()) as { content?: Array<{ type: string; text?: string }> };
  return (data.content ?? [])
    .filter((b) => b.type === 'text')
    .map((b) => b.text ?? '')
    .join('\n');
}

async function callOpenAI(apiKey: string, userMessage: string): Promise<string> {
  const r = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: 'gpt-5-mini',
      messages: [{ role: 'user', content: userMessage }],
    }),
  });
  if (!r.ok) {
    // try a fallback model
    const r2 = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [{ role: 'user', content: userMessage }],
      }),
    });
    if (!r2.ok) throw new Error(`OpenAI ${r2.status}: ${await r2.text()}`);
    const d2 = (await r2.json()) as { choices?: Array<{ message?: { content?: string } }> };
    return d2.choices?.[0]?.message?.content ?? '';
  }
  const data = (await r.json()) as { choices?: Array<{ message?: { content?: string } }> };
  return data.choices?.[0]?.message?.content ?? '';
}

function parseVerdict(text: string): VerifyResult {
  const upper = text.toUpperCase();
  let verdict: VerifyResult['verdict'] = 'unknown';
  if (/VERDICT\s*:\s*SAFE/.test(upper)) verdict = 'safe';
  else if (/VERDICT\s*:\s*UNSAFE/.test(upper)) verdict = 'unsafe';
  return { verdict, summary: text.trim() };
}
